import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Heading, Modal } from '@zillow/constellation-10';

import {
  useRentalManagerUserPropertyDetailsWebApiHandler_overview,
  useRentalManagerUserPropertySummaryWebApiHandler_listing,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { getLeadConversationPath, getLeadsPagePath } from '../../utils/leadsPagePath';
import propertyUtils from '../../utils/propertyUtils';

import { useAuth } from '../../components/AuthProvider';
import SelectAvailableActions from '../modals/AddPropertyModal/components/SelectAvailableActions';
import PostActivationDashboardModal from '../modals/PostActivationDashboardModal';

import { WrongAccountAlert } from '../Dashboard/WrongAccountAlert';
import ApplicationsModule from './modules/Applications/ApplicationsModule';
import InterestedRenters from './modules/Applications/InterestedRenters';
import UpcomingToursModule from './modules/Applications/UpcomingToursModule';
import InquiriesModule from './modules/Inquiries/InquiriesModule';
import LeasesModule from './modules/Leases/LeasesModule';
import ListingModule from './modules/Listing/ListingModule';
import NextStepsModule from './modules/NextSteps/NextStepsModule';
import TenantsModule from './modules/Tenants/TenantsModule';

import type { DatarouterError } from '@zg-rentals/web-base';

type Props = {
  listingAlias: string;
};

const OverviewPage = ({ listingAlias }: Props) => {
  const { user } = useAuth();
  const router = useRouter();
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isActivationModalOpen, setIsActivationModalOpen] = useState(false);

  useEffect(() => {
    if (router.query.showSuccessModal === 'true') {
      setIsSuccessModalOpen(true);
      const { showSuccessModal: _, ...restQuery } = router.query;
      router.replace({ pathname: router.pathname, query: restQuery }, undefined, { shallow: true });
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [router.query.showSuccessModal]);

  useEffect(() => {
    if (router.query.showActivationModal === 'true') {
      setIsActivationModalOpen(true);
      const { showActivationModal: _, ...restQuery } = router.query;
      router.replace({ pathname: router.pathname, query: restQuery }, undefined, { shallow: true });
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [router.query.showActivationModal]);

  const { data: propertyOverviewData, error } = useRentalManagerUserPropertyDetailsWebApiHandler_overview(
    { listingAlias },
    { enabled: Boolean(listingAlias) },
  );

  const { data: listingData, isSuccess: isListingSuccess } = useRentalManagerUserPropertySummaryWebApiHandler_listing(
    { listingAlias },
    {
      enabled: Boolean(listingAlias),
      meta: {
        tags: ['listingSummary'],
        context: { api: 'listing', details: { listingAlias } },
      },
    },
  );

  const isActive = propertyOverviewData?.isActive ?? false;
  // Treat an unresolved overview as archived so tenant writes stay hidden until we know.
  const isArchived = !propertyOverviewData || propertyOverviewData.isArchived;
  const isApplicationsRestricted = propertyOverviewData?.restrictions?.applications?.isAccessRestricted ?? false;
  const isLeasesRestricted = propertyOverviewData?.restrictions?.isLeasesRestricted ?? false;
  const isEnterpriseCustomer = user?.isEnterpriseCustomer ?? false;
  const isLeaseBuilderRestricted = true;
  const isRoomForRent = propertyUtils.isRoomForRent({ listing: propertyOverviewData });
  const street = propertyOverviewData?.address?.street ?? '';
  const propertyState = propertyOverviewData?.address?.state ?? '';
  const isFeed = listingData?.isFeed ?? false;
  const leadsPagePath = getLeadsPagePath(listingAlias, listingData);
  // Only link once the feed/manual answer is known: a disabled or errored query leaves listingData
  // undefined, and the helper's fallback would send a feed listing to Lead Management.
  const inquiriesPath = isListingSuccess ? leadsPagePath : undefined;
  const getConversationPath = useMemo(
    () =>
      isListingSuccess
        ? (conversationId: string) => getLeadConversationPath(listingAlias, listingData, conversationId)
        : undefined,
    [isListingSuccess, listingAlias, listingData],
  );

  const renderActiveListingModules = () => (
    <>
      <ListingModule listingAlias={listingAlias} />
      <TenantsModule listingAlias={listingAlias} isArchived={isArchived} />
      {isEnterpriseCustomer ? (
        <InquiriesModule listingAlias={listingAlias} inquiriesPath={inquiriesPath} />
      ) : (
        !isRoomForRent && (
          <InterestedRenters
            listingAlias={listingAlias}
            inquiriesPath={inquiriesPath}
            getConversationPath={getConversationPath}
            street={street}
            isFeed={isFeed}
          />
        )
      )}
      {!isApplicationsRestricted && <ApplicationsModule listingAlias={listingAlias} street={street} />}
      {!isLeasesRestricted && (
        <LeasesModule
          listingAlias={listingAlias}
          isLeaseBuilderRestricted={isLeaseBuilderRestricted}
          propertyState={propertyState}
        />
      )}
    </>
  );

  const renderInactiveListingModules = () => (
    <>
      <TenantsModule listingAlias={listingAlias} isArchived={isArchived} />
      {!isLeasesRestricted && (
        <LeasesModule
          listingAlias={listingAlias}
          isLeaseBuilderRestricted={isLeaseBuilderRestricted}
          propertyState={propertyState}
        />
      )}
      <ListingModule listingAlias={listingAlias} />
    </>
  );

  if (error && (error as DatarouterError).responseStatus === 403) {
    return <WrongAccountAlert cardLabel="Overview" />;
  }

  return (
    <>
      {isActivationModalOpen && (
        <PostActivationDashboardModal
          listingAlias={listingAlias}
          isOpen={isActivationModalOpen}
          onClose={() => setIsActivationModalOpen(false)}
        />
      )}
      {isSuccessModalOpen && (
        <Modal
          open={isSuccessModalOpen}
          onOpenChange={(open) => !open && setIsSuccessModalOpen(false)}
          scrollDocument={false}
          size={{ base: 'fullScreen', md: 'lg' }}
          header={<Heading level={2}>Zillow Rental Manager</Heading>}
          body={
            <SelectAvailableActions
              listingAlias={listingAlias}
              triggerObject="zrm_user_onboarding"
              user={user ?? undefined}
            />
          }
        />
      )}
      <Box
        data-testid="OverviewPage"
        css={{
          display: 'grid',
          gridTemplateColumns: 'repeat(12, minmax(0,1fr))',
          gap: { xxl: '400' },
        }}
      >
        {/* Left column: primary modules */}
        <Box css={{ gridColumn: { base: '1/13', xxl: '1/8' }, display: 'flex', flexDirection: 'column', gap: '400' }}>
          {isActive ? renderActiveListingModules() : renderInactiveListingModules()}
        </Box>

        {/* Right column: UpcomingTours + NextSteps */}
        <Box
          css={{
            gridColumn: { base: '1/13', xxl: '8/13' },
            gridRowStart: { base: '1', xxl: 'inherit' },
            marginBottom: { base: '400', xxl: 0 },
            display: 'flex',
            flexDirection: 'column',
            gap: '400',
          }}
        >
          <UpcomingToursModule listingAlias={listingAlias} isActive={isActive} inquiriesPath={leadsPagePath} />
          <NextStepsModule listingAlias={listingAlias} />
        </Box>
      </Box>
    </>
  );
};

export default OverviewPage;
